/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Chapeau } from '@datacamp/waffles/chapeau';
import { Dialog } from '@datacamp/waffles/dialog';
import { FormField } from '@datacamp/waffles/form-field';
import {
  FacebookBrand,
  GoogleColoredBrand,
  LinkedInBrand,
} from '@datacamp/waffles/icon';
import { Input } from '@datacamp/waffles/input';
import { Link } from '@datacamp/waffles/link';
import { Paragraph } from '@datacamp/waffles/paragraph';
import { Text } from '@datacamp/waffles/text';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { I18N_PLACEHOLDER } from '../../i18n';

const linkButtonStyle = {
  width: 120,
  ':hover': {
    color: tokens.colors.navy,
  },
};

const strikethroughStyle = {
  display: 'flex',
  flexDirection: 'row',
  marginTop: tokens.spacing.medium,
  ':before, :after': {
    content: '""',
    flex: '1 1',
    borderBottom: `${tokens.borderWidth.thin} solid ${tokens.colors.transparentGrey}`,
    margin: 'auto',
  },
  ':before': {
    marginRight: tokens.spacing.small,
  },
  ':after': {
    marginLeft: tokens.spacing.small,
  },
} as const;

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing.medium,
  marginTop: tokens.spacing.medium,
} as const;

const linkStyle = {
  borderBottom: 0,
};

type Props = {
  datacampUrl?: string;
};

const SignupForm: React.FC<Props> = ({
  datacampUrl = 'https://www.datacamp.com',
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { t } = useTranslation(['translation', 'common']);

  return (
    <>
      <Dialog.Header>
        <Chapeau css={{ color: tokens.colors.blueDarkText }}>
          {t('SignupForm.chapeau')}
        </Chapeau>
        <span css={{ fontSize: tokens.fontSizes.huge }}>
          {t('SignupForm.header')}
        </span>
      </Dialog.Header>
      <Dialog.Body>
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            gap: tokens.spacing.small,
            marginTop: tokens.spacing.xsmall,
          }}
        >
          <Button
            variant="secondary"
            iconLeft={<GoogleColoredBrand />}
            as="a"
            href={`${datacampUrl}/users/auth/google_oauth2?redirect=${window.location.href}`}
            css={linkButtonStyle}
          >
            Google
          </Button>
          <Button
            variant="secondary"
            iconLeft={
              <LinkedInBrand css={{ color: tokens.colors.brandLinkedIn }} />
            }
            as="a"
            href={`${datacampUrl}/users/auth/linkedin?redirect=${window.location.href}`}
            css={linkButtonStyle}
          >
            LinkedIn
          </Button>
          <Button
            variant="secondary"
            iconLeft={
              <FacebookBrand css={{ color: tokens.colors.brandFacebook }} />
            }
            as="a"
            href={`${datacampUrl}/users/auth/facebook?redirect=${window.location.href}`}
            css={linkButtonStyle}
          >
            Facebook
          </Button>
        </div>
        <Text as="p" css={strikethroughStyle}>
          {t('common:or')}
        </Text>
        <form
          acceptCharset="UTF-8"
          action={`${datacampUrl}/users`}
          id="new_user"
          method="post"
          css={formStyle}
          data-cy="login-form"
        >
          <input name="utf8" type="hidden" value="✓" />
          <input
            id="how"
            name="how"
            type="hidden"
            value="le_shared_create_account_modal"
          />
          <input
            id="redirect"
            name="redirect"
            type="hidden"
            value={window.location.href}
          />
          <FormField label={t('SignupForm.emailAddress')} required>
            <Input
              id="user_email"
              name="user[email]"
              onChange={(event) => setEmail(event.target.value)}
              value={email}
              type="email"
            />
          </FormField>
          <FormField label={t('SignupForm.password')} required>
            <Input
              id="user_password"
              name="user[password]"
              onChange={(event) => setPassword(event.target.value)}
              value={password}
              type="password"
            />
          </FormField>
          <Button
            variant="primary"
            type="submit"
            css={{ marginTop: tokens.spacing.small }}
          >
            {t('SignupForm.primaryButton')}
          </Button>
        </form>
        <Paragraph
          size="small"
          variant="secondary"
          css={{
            marginTop: tokens.spacing.medium,
            marginBottom: 0,
          }}
        >
          <Trans
            t={t}
            i18nKey="SignupForm.legalSnippet"
            components={{
              termsLink: (
                <Link
                  href={`${datacampUrl}/terms-of-use`}
                  rel="noopener noreferrer"
                  target="_blank"
                  css={linkStyle}
                >
                  {I18N_PLACEHOLDER}
                </Link>
              ),
              privacyPolicyLink: (
                <Link
                  href={`${datacampUrl}/privacy-policy`}
                  rel="noopener noreferrer"
                  target="_blank"
                  css={linkStyle}
                >
                  {I18N_PLACEHOLDER}
                </Link>
              ),
            }}
          />
        </Paragraph>
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Button
          variant="secondary"
          as="a"
          href={`${datacampUrl}/users/sign_in?redirect=${window.location.href}`}
          css={linkButtonStyle}
        >
          {t('SignupForm.secondaryButton')}
        </Dialog.Button>
      </Dialog.Footer>
    </>
  );
};

export default SignupForm;
